import dayjs from 'dayjs'
import WealthboxTasksContent from './WealthboxTasksContent'
import WealthboxNotesContent from './WealthboxNotesContent'
import WealthboxEventsContent from './WealthboxEventsContent'

// Preset registry for the `wealthbox-list` component type. Mirrors the
// `wealthboxMetric/presets.js` shape so vocabulary stays consistent across
// both components - blueprint authors switching between a metric tile and a
// list of the same data use the same `preset` key.
//
// IMPORTANT - "PAST DUE" / "DUE WITHIN N DAYS" PRESETS ARE NOT YET SHIPPED:
// SMT-1355's Jira AC text calls for those two presets, but Wealthbox's
// /v1/tasks endpoint has no server-side `due_date` filter or sort param (see
// .plans/SMT-1352.md for the full investigation). Until we either build the
// client-side pagination workaround or Wealthbox adds support, we ship the
// three presets the API can actually deliver: open / recently-updated /
// stale. The preset definitions below are structured so dropping in a Past
// Due preset later is a localized add (new entry in `presets`, new
// `buildParams` branch).

// Each preset declares which `config.*` fields its API call actually consumes
// via `usesFields`. The ConfigEditor uses this to hide irrelevant inputs.

function buildFilterParams (config) {
  const params = {}
  if (config?.includeCompleted) params.completed = true
  if (config?.taskType) params.task_type = config.taskType
  if (config?.teamId != null && config.teamId !== '') {
    const teamId = Number(config.teamId)
    if (Number.isFinite(teamId)) params.assigned_to_team = teamId
  }
  return params
}

// Top-level fields on the listTasks request that the server translates
// against the resolved token (scope, createdByScope). Kept separate from
// `params` so the schema validation on the server matches.
function buildScopeFields (config) {
  const fields = {}
  if (config?.scope) fields.scope = config.scope
  if (config?.createdByScope) fields.createdByScope = config.createdByScope
  return fields
}

const TASK_DEFAULTS = {
  icon: 'wealthboxSmall',
  enableEdit: false,
  enableCreate: false,
  formMode: 'simple',
  limit: 5,
  // Domain identifies which entity the preset operates on. Drives the
  // server-side query name (`listTasks` vs `listNotes` vs `listEvents`) in
  // useWealthboxListPreset.
  domain: 'tasks'
}

const NOTE_DEFAULTS = {
  icon: 'wealthboxSmall',
  enableEdit: false,
  enableCreate: false,
  limit: 5,
  previewLength: 200,
  domain: 'notes'
}

const EVENT_DEFAULTS = {
  icon: 'wealthboxSmall',
  enableEdit: false,
  enableCreate: false,
  formMode: 'calendar',
  limit: 5,
  lookaheadDays: 90,
  domain: 'events'
}

const openTasks = {
  key: 'open-tasks',
  displayName: 'Open Tasks',
  usesFields: [],
  defaults: { ...TASK_DEFAULTS, title: 'Open Tasks' },
  buildRequest (config) {
    return {
      params: buildFilterParams(config),
      scopeFields: buildScopeFields(config)
    }
  },
  Content: WealthboxTasksContent
}

const recentlyUpdatedTasks = {
  key: 'recently-updated-tasks',
  displayName: 'Recently Updated Tasks',
  usesFields: ['updatedWithinDays'],
  defaults: { ...TASK_DEFAULTS, title: 'Recent Activity' },
  buildRequest (config) {
    const updatedWithinDays = config?.updatedWithinDays ?? 7
    return {
      params: {
        ...buildFilterParams(config),
        updated_since: dayjs().subtract(updatedWithinDays, 'day').toISOString()
      },
      scopeFields: buildScopeFields(config)
    }
  },
  Content: WealthboxTasksContent
}

const staleTasks = {
  key: 'stale-tasks',
  displayName: 'Stale Tasks',
  usesFields: ['staleAfterDays'],
  defaults: { ...TASK_DEFAULTS, title: 'Stale Tasks' },
  buildRequest (config) {
    const staleAfterDays = config?.staleAfterDays ?? 30
    return {
      params: {
        ...buildFilterParams(config),
        updated_before: dayjs().subtract(staleAfterDays, 'day').toISOString()
      },
      scopeFields: buildScopeFields(config)
    }
  },
  Content: WealthboxTasksContent
}

// Notes don't have the rich task filter set - no completed/task_type/teamId
// filters apply server-side. The notes preset just translates scope (server
// auto-fills `created_by`) and optionally narrows by updated_since.
function buildNoteFilterParams (config) {
  // Notes don't support assigned_to_team, completed, task_type, etc. Only
  // updated-time-based filters work as documented. Leave empty - the scope
  // and createdByScope fields handle the assignee-side filtering server-side.
  return {}
}

const latestNotes = {
  key: 'latest-notes',
  displayName: 'Latest Notes',
  usesFields: ['order'],
  defaults: { ...NOTE_DEFAULTS, title: 'Latest Notes' },
  buildRequest (config) {
    // Wealthbox notes order enum: 'asc' | 'created' | 'updated'.
    // Default 'updated' (newest activity first - matches admin intent for
    // "Latest Notes").
    const order = config?.order || 'updated'
    return {
      params: { ...buildNoteFilterParams(config), order },
      scopeFields: buildScopeFields(config)
    }
  },
  Content: WealthboxNotesContent
}

// Events are the one Wealthbox list type where server-side date filtering +
// sorting work as documented. We can deliver the "Upcoming Meetings" preset
// from the ticket without client-side workarounds: pass start_date_min=today
// + start_date_max=today+lookahead + order=asc and Wealthbox returns the
// pre-filtered, pre-sorted set.
const upcomingMeetings = {
  key: 'upcoming-meetings',
  displayName: 'Upcoming Meetings',
  usesFields: ['lookaheadDays', 'order'],
  defaults: { ...EVENT_DEFAULTS, title: 'Upcoming Meetings' },
  buildRequest (config) {
    const lookaheadDays = config?.lookaheadDays ?? 90
    // Wealthbox events order enum: 'asc' | 'desc' | 'recent' | 'created'.
    // Default 'asc' (chronological — earliest meeting first).
    const order = config?.order || 'asc'
    return {
      params: {
        start_date_min: dayjs().format('YYYY-MM-DD'),
        start_date_max: dayjs().add(lookaheadDays, 'day').format('YYYY-MM-DD'),
        order
      },
      scopeFields: buildScopeFields(config)
    }
  },
  Content: WealthboxEventsContent
}

const presets = {
  'open-tasks': openTasks,
  'recently-updated-tasks': recentlyUpdatedTasks,
  'stale-tasks': staleTasks,
  'latest-notes': latestNotes,
  'upcoming-meetings': upcomingMeetings
}

export default presets
